import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Package,
  Thermometer,
  Truck,
  X,
} from 'lucide-react';

import { kitsApi } from '../kits.api.js';
import { kitQueryKeys } from '../kits.query-keys.js';

export interface ShipmentsHistoryDialogProps {
  trialId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShipmentsHistoryDialog({
  trialId,
  open,
  onOpenChange,
}: ShipmentsHistoryDialogProps): JSX.Element | null {
  const [expandedShipmentId, setExpandedShipmentId] = useState<number | null>(null);

  const shipmentsQuery = useQuery({
    queryKey: kitQueryKeys.shipments.byTrial(trialId),
    queryFn: () => kitsApi.listShipments({ trialId, page: 1, limit: 50 }),
    enabled: open && trialId > 0,
    staleTime: 60 * 1000,
  });

  const shipments = shipmentsQuery.data?.items ?? [];

  function toggleExpand(id: number): void {
    setExpandedShipmentId((prev) => (prev === id ? null : id));
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="shipments-history-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-950 text-cyan-400 border border-cyan-800/60">
              <Truck className="h-5 w-5" />
            </div>
            <div>
              <h2 id="shipments-history-title" className="text-lg font-semibold text-slate-100">
                Historia przesyłek i dostaw materiałów
              </h2>
              <p className="text-xs text-slate-400">
                Wykaz przyjętych paczek od kurierów wraz z pozycjami i warunkami transportu
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            aria-label="Zamknij"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {shipmentsQuery.isLoading ? (
            <div className="flex h-48 items-center justify-center text-sm text-slate-400">
              Ładowanie historii przesyłek...
            </div>
          ) : shipments.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-center text-slate-400">
              <Package className="h-10 w-10 text-slate-600 mb-2" />
              <p className="text-sm font-medium text-slate-300">Brak zarejestrowanych przesyłek</p>
              <p className="text-xs text-slate-500 mt-1">
                Użyj przycisku „Przyjmij przesyłkę”, aby zarejestrować pierwszą dostawę od kuriera.
              </p>
            </div>
          ) : (
            <div className="space-y-3" id="shipments-history-list">
              {shipments.map((shipment) => {
                const isExpanded = expandedShipmentId === shipment.id;
                const itemsCount = shipment.items?.length ?? 0;
                const totalPieces = shipment.items?.reduce((s, i) => s + i.quantity, 0) ?? 0;

                return (
                  <div
                    key={shipment.id}
                    data-testid={`shipment-row-${shipment.id}`}
                    className="rounded-lg border border-slate-800 bg-slate-950/60 overflow-hidden"
                  >
                    <div
                      onClick={() => toggleExpand(shipment.id)}
                      className="flex items-center justify-between px-4 py-3 cursor-pointer select-none hover:bg-slate-800/40"
                    >
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-slate-400 hover:text-cyan-400"
                          aria-label={isExpanded ? 'Zwiń szczegóły' : 'Rozwiń szczegóły'}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>

                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-100 font-mono text-sm">
                              {shipment.shipmentNumber}
                            </span>
                            {shipment.courierName && (
                              <span className="text-xs text-slate-400">
                                ({shipment.courierName})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                            <span className="flex items-center gap-1 font-mono">
                              <Calendar className="h-3 w-3 text-slate-500" />
                              <span>{shipment.receptionDate}</span>
                            </span>
                            {shipment.comments && (
                              <span className="italic truncate max-w-md text-slate-500">
                                „{shipment.comments}”
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1.5">
                          <Thermometer className="h-3.5 w-3.5 text-slate-500" />
                          {shipment.temperatureOk ? (
                            <span className="inline-flex items-center gap-1 text-emerald-400">
                              <CheckCircle2 className="h-3 w-3" />
                              <span>Temp. zgodna ({shipment.temperatureCondition ?? 'ambient'})</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rose-400 font-medium">
                              <AlertCircle className="h-3 w-3" />
                              <span>Przekroczenie temp.!</span>
                            </span>
                          )}
                        </div>

                        <div className="text-right font-mono">
                          <span className="text-slate-200 font-semibold">{totalPieces} szt.</span>
                          <span className="text-slate-500 text-[11px] block">
                            ({itemsCount} {itemsCount === 1 ? 'pozycja' : 'pozycje'})
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="border-t border-slate-800/80 bg-slate-900/90 px-6 py-3">
                        <table className="w-full text-xs text-left">
                          <thead className="text-[11px] text-slate-400 uppercase border-b border-slate-800 pb-1">
                            <tr>
                              <th className="py-1.5">Zestaw</th>
                              <th className="py-1.5">Partia / Seria</th>
                              <th className="py-1.5">Data ważności</th>
                              <th className="py-1.5 text-right">Ilość</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/50 font-mono">
                            {shipment.items?.map((item, idx) => (
                              <tr key={idx} className="text-slate-300">
                                <td className="py-2 font-sans font-medium text-slate-100">
                                  {item.kitName ?? `Kit #${item.labKitId}`}
                                </td>
                                <td className="py-2 text-slate-400">
                                  {item.batchNumber ?? '—'}
                                </td>
                                <td className="py-2 text-slate-400">
                                  {item.expirationDate ?? 'Bezterminowy'}
                                </td>
                                <td className="py-2 text-right font-semibold text-cyan-300">
                                  {item.quantity} szt.
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-slate-800 px-6 py-3 bg-slate-950/60 rounded-b-xl">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
}
